import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { brand } from "../mock";
import { publicApi } from "../lib/api";
import { useContent } from "../lib/useContent";

const Footer = () => {
  const [categories, setCategories] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const { field } = useContent("footer");

  useEffect(() => {
    publicApi.listCategories().then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
    publicApi.getSiteConfig?.().then((r) => setSiteSettings(r.data || null)).catch(() => setSiteSettings(null));
  }, []);

  // Intro block
  const brandSubtitle = field("intro", "brand_subtitle", "Est. 2013 · Sardegna");
  const introHeadline = field("intro", "headline", "Un intero centro cittadino, a portata di passeggiata.");
  const introDescription = field("intro", "description", "Il Centro Commerciale Naturale di Sanluri unisce commercianti, artigiani, professionisti e associazioni per far vivere il cuore del paese ogni giorno dell'anno.");

  // Columns
  const exploreTitle = field("column_explore", "title", "Esplora");
  const exploreItemsRaw = field("column_explore", "items", null);
  const exploreItems = Array.isArray(exploreItemsRaw) && exploreItemsRaw.length
    ? exploreItemsRaw.filter((i) => i.visible !== false)
    : [
        { label: "Home", url: "/" },
        { label: "Chi siamo", url: "/chi-siamo" },
        { label: "Attività", url: "/attivita" },
        { label: "Eventi", url: "/eventi" },
        { label: "Contatti", url: "/contatti" },
      ];
  const categoriesTitle = field("column_categories_title", "title", "Categorie");
  const contactsTitle = field("column_contacts_title", "title", "Contatti");

  // Legal / copyright / credit
  const privacyLabel = field("legal_links", "privacy_label", "Privacy Policy");
  const privacyUrl = field("legal_links", "privacy_url", "https://www.iubenda.com/privacy-policy/60997306");
  const cookieLabel = field("legal_links", "cookie_label", "Cookie Policy");
  const cookieUrl = field("legal_links", "cookie_url", "https://www.iubenda.com/privacy-policy/60997306/cookie-policy");

  const copyrightTemplate = field("copyright", "template", "© {year} {name}. Tutti i diritti riservati.");
  const copyrightName = field("copyright", "name", brand.fullName);
  const copyright = copyrightTemplate
    .replace("{year}", String(new Date().getFullYear()))
    .replace("{name}", copyrightName);

  const creditPrefix = field("credit", "prefix", "Sito web sviluppato da ");
  const creditLabel = field("credit", "label", "Labix Web Agency");
  const creditUrl = field("credit", "url", "https://www.agenzialabix.it");
  const creditVisible = field("credit", "visible", true);

  // Contacts / socials — from site_settings (single source of truth)
  const address = siteSettings?.contact_address || brand.address;
  const phone = siteSettings?.contact_phone || brand.phone;
  const email = siteSettings?.contact_email || brand.email;
  const socials = [
    { url: siteSettings?.instagram_url, Icon: Instagram, label: "Instagram" },
    { url: siteSettings?.facebook_url, Icon: Facebook, label: "Facebook" },
    { url: siteSettings?.youtube_url, Icon: Youtube, label: "YouTube" },
    { url: siteSettings?.linkedin_url, Icon: Linkedin, label: "LinkedIn" },
  ].filter((s) => s.url && s.url.trim());

  return (
    <footer className="bg-stone-950 text-stone-200 mt-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-16 border-b border-stone-800">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-[#12964A] flex items-center justify-center text-white font-display text-xl">C</div>
              <div>
                <div className="font-display text-xl">{siteSettings?.site_name || "CCN Sanluri"}</div>
                {brandSubtitle !== "" && <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500">{brandSubtitle}</div>}
              </div>
            </div>
            {introHeadline !== "" && (
              <h3 className="font-display text-3xl md:text-4xl leading-[1.15] mb-6 max-w-md">
                {introHeadline}
              </h3>
            )}
            {introDescription !== "" && (
              <p className="text-stone-400 max-w-md leading-relaxed">{introDescription}</p>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-5">{exploreTitle}</div>
            <ul className="space-y-3">
              {exploreItems.map((item) => (
                <li key={item.url}>
                  <Link to={item.url} className="hover:text-[#6FC247] transition-colors">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-5">{categoriesTitle}</div>
            <ul className="space-y-3">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link to={`/categoria/${c.slug}`} className="hover:text-[#6FC247] transition-colors">{c.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-5">{contactsTitle}</div>
            <ul className="space-y-4 text-stone-300">
              {address && (
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-[#6FC247]" />
                  <span>{address}</span>
                </li>
              )}
              {phone && (
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-[#6FC247]" />
                  <a href={`tel:${phone}`} className="hover:text-[#6FC247]">{phone}</a>
                </li>
              )}
              {email && (
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#6FC247]" />
                  <a href={`mailto:${email}`} className="hover:text-[#6FC247]">{email}</a>
                </li>
              )}
            </ul>
            {socials.length > 0 && (
              <div className="flex items-center gap-3 mt-6">
                {socials.map(({ url, Icon, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="w-10 h-10 rounded-full border border-stone-700 flex items-center justify-center hover:bg-[#12964A] hover:border-[#12964A] transition-colors"
                    data-testid={`footer-social-${label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10 text-sm text-stone-500">
          <div data-testid="footer-copyright">{copyright}</div>
          <div className="flex items-center gap-6">
            <a href={privacyUrl} target="_blank" rel="noreferrer" className="hover:text-stone-200 inline-flex items-center gap-1">
              {privacyLabel} <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
            <a href={cookieUrl} target="_blank" rel="noreferrer" className="hover:text-stone-200 inline-flex items-center gap-1">
              {cookieLabel} <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {creditVisible !== false && creditLabel !== "" && (
          <div className="pt-6 text-center text-xs text-stone-600">
            {creditPrefix}
            <a href={creditUrl} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-[#6FC247] underline-offset-4 hover:underline transition-colors">
              {creditLabel}
            </a>
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
